import moment from "moment";
import { InvoiceApi } from "@/api/InvoiceApi";
import { EDOCUMENT_SUBMITION_STATUS_CODE, EDOCUMENT_SUBMITION_STATUS_SENT } from "@/atv/eInvoiceApi";
import { CURRENCIES_CODE_IDS } from "@utils/Constants";
import { formatMoneyValue } from "@utils/Currency";
import { serializeParsedReferences } from "../core/storedReferences";
import { serializeParsedOtherCharges } from "../core/storedOtherCharges";
import {
    EDocumentImportDefinition,
    EDocumentImportUiCopy,
    ImportEDocumentType,
    ParsedEDocument,
    ReferenceableDocument,
    ResolvedReferences,
} from "../core/types";

export const CREDITNOTE_ROOT_NAME = "NotaCreditoElectronica";
export const DEBITNOTE_ROOT_NAME = "NotaDebitoElectronica";

/** Largo de sucursal + terminal + tipo dentro del consecutivo de 20 digitos. */
const SEQUENCE_PREFIX_LENGTH = 10;

/** Formato con el que el sistema guarda `created` en las notas. */
const CREATED_FORMAT = "YYYY-MM-DD HH:mm:ss";

/**
 * Estado con el que quedan las notas cargadas: ya fueron aceptadas por Hacienda en el sistema
 * anterior, asi que la carga las marca como enviadas y aceptadas sin volver a emitirlas.
 */
export const IMPORTED_ATV_STATUS_PAYLOAD = JSON.stringify({ status: "aceptado" });

/** Descripcion de respaldo cuando ninguna referencia del XML trae razon. */
const FALLBACK_DESCRIPTION = "Nota cargada desde XML";

/** `Invoice.status` de una factura ya cobrada; misma comparacion que hace `save-edocument`. */
export const PAID_INVOICE_STATUS = "pagada";

/** La parte consecutiva del `NumeroConsecutivo`, que es lo que el modelo guarda en `numero`. */
export const noteNumberFromSequence = (sequence: string): number =>
    Number(`${sequence || ""}`.slice(SEQUENCE_PREFIX_LENGTH)) || 0;

/** Razon de la primera referencia que traiga alguna. */
const describeNote = (parsed: ParsedEDocument): string =>
    parsed.referencias.find((reference) => `${reference.razon}`.trim())?.razon || FALLBACK_DESCRIPTION;

/** Posicion del codigo de tipo de comprobante dentro del consecutivo de 20 digitos. */
const SEQUENCE_TYPE_RANGE: [number, number] = [8, 10];

type NoteImportConfig = {
    documentType: Extract<ImportEDocumentType, "Creditnote" | "Debitnote">;
    rootName: string;
    ui: EDocumentImportUiCopy;
    /** Codigo de tipo de comprobante dentro del consecutivo: "03" nota de credito, "02" debito. */
    sequenceTypeCode: string;
    loadExistingKeys: () => Promise<Set<string>>;
    invalidateCaches: () => Promise<void>;
    /** Lo poco que distingue a una nota de credito de una de debito en la cabecera. */
    extraHeader: (referenced?: ReferenceableDocument) => Record<string, unknown>;
};

/**
 * Construye la definicion de carga de una nota de credito o de debito.
 *
 * Las dos comparten cabecera, lineas, columnas y reglas: solo cambian el tipo, la raiz XML, los
 * textos y un campo del modelo. Se factoriza aqui para que agregar o corregir algo no haya que
 * hacerlo dos veces.
 */
export const buildNoteImportDefinition = (config: NoteImportConfig): EDocumentImportDefinition => {
    const buildHeader = (
        parsed: ParsedEDocument,
        refs: ResolvedReferences
    ): Record<string, unknown> => {
        const referenced = refs.referencedDocument;

        return {
            document_key: parsed.clave,
            sequence: parsed.numeroConsecutivo,
            numero: noteNumberFromSequence(parsed.numeroConsecutivo),
            // `parseZone` conserva la hora con la que se emitio el comprobante. Con `moment()`
            // la fecha se moveria a la zona de la maquina que hace la carga, y una nota emitida
            // de noche podria quedar registrada al dia siguiente.
            created: moment.parseZone(parsed.fechaEmision).format(CREATED_FORMAT),
            description: describeNote(parsed),

            edocument_status: EDOCUMENT_SUBMITION_STATUS_CODE[EDOCUMENT_SUBMITION_STATUS_SENT],
            edocument_status_payload: IMPORTED_ATV_STATUS_PAYLOAD,
            anulada: false,
            price_note: false,
            excludeFromReport: false,
            selected_logo: 0,

            // La nota hereda el cliente de su factura: el modelo no guarda snapshot del receptor
            // y el `Receptor` es opcional en el XSD de las notas.
            invoiceId: referenced?.id,
            clientId: refs.clientId,
            currencyId: refs.currencyId,
            exchange_rate: refs.exchangeRate,

            activitycode: parsed.codigoActividadEmisor,
            email: parsed.receptor?.correo,

            subtotal: parsed.resumen.totalVenta,
            descuento: parsed.resumen.totalDescuentos,
            impuestos: parsed.resumen.totalImpuesto,
            impuestos_servicio: 0,
            total: parsed.resumen.totalComprobante,

            info_references: serializeParsedReferences(parsed, referenced),
            // Con la forma de `StoredOtherCharge`, no la del parser: `parseStoredOtherCharges`
            // descarta toda entrada que no la tenga y el documento se imprime sin cargos.
            otros_cargos: serializeParsedOtherCharges(parsed),

            ...config.extraHeader(referenced),
        };
    };

    return {
        documentType: config.documentType,
        acceptedRootNames: [config.rootName],
        ui: config.ui,

        loadExistingKeys: config.loadExistingKeys,

        requiresReference: {
            loadDocuments: async (): Promise<ReferenceableDocument[]> => {
                const invoices = await InvoiceApi.fetchAllReferenceable();
                return invoices
                    .filter((invoice) => !!invoice.id)
                    .map((invoice) => ({
                        id: `${invoice.id}`,
                        key: `${invoice.invoice_key || ""}`,
                        sequence: `${invoice.sequence || ""}`,
                        numero: Number(invoice.numero) || 0,
                        clientId: invoice.clientId ? `${invoice.clientId}` : undefined,
                        status: invoice.status ? `${invoice.status}` : undefined,
                        fecha: invoice.fecha ? `${invoice.fecha}` : undefined,
                    }));
            },
            missingMessage: (reference) =>
                reference
                    ? `No se encontro en el sistema la factura referenciada (${reference}). Cargue primero esa factura y vuelva a intentar.`
                    : "La nota no referencia ninguna factura del sistema. Cargue primero la factura correspondiente y vuelva a intentar.",
        },

        buildHeader,

        /**
         * El consecutivo declara su propio tipo de comprobante. Que no coincida con el tipo que
         * se esta cargando delata un consecutivo mal formado; se avisa sin bloquear porque la
         * raiz del XML, que ya se valido, es la fuente autoritativa.
         */
        extraWarnings: (parsed) => {
            const declared = `${parsed.numeroConsecutivo || ""}`.slice(...SEQUENCE_TYPE_RANGE);
            return declared && declared !== config.sequenceTypeCode
                ? [`El consecutivo declara el tipo de comprobante ${declared} y no ${config.sequenceTypeCode}`]
                : [];
        },

        // Las notas no tienen modelo de pagos: no existen `CreditnotePayment` ni `DebitnotePayment`.
        buildPayments: () => [],

        previewColumns: [
            { header: "Consecutivo", render: (row) => row.parsed.numeroConsecutivo },
            {
                header: "Fecha",
                render: (row) =>
                    row.parsed.fechaEmision ? moment(row.parsed.fechaEmision).format("DD-MM-YYYY") : "",
            },
            {
                header: "Factura",
                render: (row) =>
                    row.referencedDocument
                        ? `#${row.referencedDocument.numero}`
                        : "Sin factura en el sistema",
            },
            {
                header: "Total",
                render: (row) =>
                    formatMoneyValue(
                        row.parsed.resumen.totalComprobante,
                        CURRENCIES_CODE_IDS[row.parsed.resumen.codigoMoneda]
                    ),
            },
        ],

        invalidateCaches: config.invalidateCaches,
    };
};
